/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview Voice session controls component (join/leave/mute/mode)
 * @version 1.0.0
 * @since v0.0.07
 *
 * Purpose: Web UI controls for join/leave voice, mute/unmute, and mode selection
 * (transcribe_all / wake_word / off) - parity with Discord commands/buttons.
 *
 * Per FRONTEND_TODO.MD:
 * - Add UI components + WS messages mirroring backend modes
 * - Reuse contract defaults
 * - Reflect current state from server pushes
 *
 * Dependencies:
 * - websocketService for sending mode changes
 * - AudioProcessor for mic capture
 * - VoicePlaybackService for audio playback
 * - audioSettings for configuration
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { webSocketService } from '../../services/websocket/websocketService';
import { audioProcessor } from '../../integration/AudioProcessor';
import { voicePlaybackService, BufferPreset, BUFFER_PRESETS } from '../../services/voice';
import {
  VOICE_BRIDGE_CONFIG,
  VOICE_CONNECTION_STATE,
  getAllJitterBufferPresets,
  BUFFER_PROFILE_MESSAGE,
  type VoiceConnectionState,
  type JitterBufferPresetId
} from '../../config/audioSettings';
// Added 2026-02-23: import formatMessage for volume label template interpolation
// Updated 2026-02-23: import inputMode strings for PTT/VA toggle (Section 5.5)
import { voiceControls, formatMessage } from '../../i18n/voiceMessages';
import { voiceToastService } from '../../services/toast/voiceToastService';

/**
 * Voice listening modes (matches backend contract)
 * Per voice-classification.yaml
 */
export type VoiceMode = 'transcribe_all' | 'wake_word' | 'off';

export interface VoiceModeConfig {
  id: VoiceMode;
  label: string;
  icon: string;
  description: string;
}

export const VOICE_MODES: Record<VoiceMode, VoiceModeConfig> = {
  transcribe_all: {
    id: 'transcribe_all',
    label: voiceControls.modes.transcribe_all.label,
    icon: voiceControls.modes.transcribe_all.icon,
    description: voiceControls.modes.transcribe_all.description
  },
  wake_word: {
    id: 'wake_word',
    label: voiceControls.modes.wake_word.label,
    icon: voiceControls.modes.wake_word.icon,
    description: voiceControls.modes.wake_word.description
  },
  off: {
    id: 'off',
    label: voiceControls.modes.off.label,
    icon: voiceControls.modes.off.icon,
    description: voiceControls.modes.off.description
  }
};

/**
 * Voice input mode (Section 5.5: PTT/VA Toggle)
 * Updated 2026-02-23: Push-to-Talk vs Voice Activation selection
 *
 * - 'voice_activation': Client-side VAD (voice activity detection) auto-transmits
 *   when speech is detected. This is the default mode.
 * - 'push_to_talk': Audio only transmitted while the PTT key (Space) is held down.
 *   Requires user to hold a key or mouse button to transmit.
 */
export type VoiceInputMode = 'voice_activation' | 'push_to_talk';

export interface VoiceControlsProps {
  /** Session ID for the voice session */
  sessionId: string;
  /** User/participant ID */
  participantId: string;
  /** Optional callback when connection state changes */
  onConnectionStateChange?: (state: VoiceConnectionState) => void;
  /** Optional callback when mode changes */
  onModeChange?: (mode: VoiceMode) => void;
  /** Optional callback when mute state changes */
  onMuteChange?: (isMuted: boolean) => void;
  /** Optional callback when volume changes */
  onVolumeChange?: (volume: number) => void;
  /** Optional callback when input mode changes (PTT/VA) */
  onInputModeChange?: (mode: VoiceInputMode) => void;
  /** Optional CSS class name */
  className?: string;
  /** Show buffer preset selector (default: false) */
  showBufferPreset?: boolean;
  /** Show volume slider (default: true) */
  showVolumeSlider?: boolean;
  /** Compact mode (icons only, default: false) */
  compact?: boolean;
}

/**
 * localStorage keys for persisting voice settings
 */
const VOICE_SETTINGS_KEYS = {
  MUTE: 'mythbound_voice_muted',
  VOLUME: 'mythbound_voice_volume',
  // Updated 2026-02-23: Persist PTT/VA selection across sessions (Section 5.5)
  INPUT_MODE: 'mythbound_voice_input_mode',
} as const;

/**
 * Load a persisted voice setting from localStorage
 */
function loadVoiceSetting<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return fallback;
    return JSON.parse(stored) as T;
  } catch {
    return fallback;
  }
}

/**
 * Persist a voice setting to localStorage
 */
function saveVoiceSetting(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage may be unavailable (private mode, quota exceeded)
  }
}

/**
 * VoiceControls Component
 *
 * Provides UI controls for voice session management:
 * - Join/Leave voice session
 * - Mute/Unmute microphone
 * - Select listening mode (transcribe_all, wake_word, off)
 * - Optional buffer preset selection
 *
 * Mirrors Discord voice control buttons per FRONTEND_TODO.MD
 */
export const VoiceControls: React.FC<VoiceControlsProps> = ({
  sessionId,
  participantId,
  onConnectionStateChange,
  onModeChange,
  onMuteChange,
  onVolumeChange,
  onInputModeChange,
  className = '',
  showBufferPreset = false,
  showVolumeSlider = true,
  compact = false
}) => {
  // Connection state
  const [connectionState, setConnectionState] = useState<VoiceConnectionState>(
    VOICE_CONNECTION_STATE.DISCONNECTED
  );
  const [isJoined, setIsJoined] = useState(false);

  // Audio state - restore persisted values from localStorage
  const [isMuted, setIsMuted] = useState(() =>
    loadVoiceSetting<boolean>(VOICE_SETTINGS_KEYS.MUTE, false)
  );
  const [volume, setVolume] = useState(() =>
    loadVoiceSetting<number>(VOICE_SETTINGS_KEYS.VOLUME, 1.0)
  );
  const [voiceMode, setVoiceMode] = useState<VoiceMode>('transcribe_all');
  const [bufferPreset, setBufferPreset] = useState<JitterBufferPresetId>(
    VOICE_BRIDGE_CONFIG.DEFAULT_BUFFER_PRESET
  );

  // UI state
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Updated 2026-02-23: PTT/VA input mode state (Section 5.5)
  // Persisted to localStorage so users keep their preference across sessions.
  const [inputMode, setInputMode] = useState<VoiceInputMode>(() =>
    loadVoiceSetting<VoiceInputMode>(VOICE_SETTINGS_KEYS.INPUT_MODE, 'voice_activation')
  );
  // Track whether PTT key is currently held (only relevant in push_to_talk mode)
  const [isPTTActive, setIsPTTActive] = useState(false);
  const pttActiveRef = useRef(false); // ref for event handler closure

  // Apply persisted volume on mount and whenever volume changes
  const volumeRef = useRef(volume);
  useEffect(() => {
    volumeRef.current = volume;
    voicePlaybackService.setVolume(volume);
    saveVoiceSetting(VOICE_SETTINGS_KEYS.VOLUME, volume);
    onVolumeChange?.(volume);
  }, [volume, onVolumeChange]);

  // -------------------------------------------------------------------------
  // PTT KEY HANDLING (Section 5.5)
  // Updated 2026-02-23: Space key acts as push-to-talk when in PTT mode.
  // KeyDown starts capture, KeyUp stops capture. Only active when joined +
  // input mode is push_to_talk + not muted.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (inputMode !== 'push_to_talk' || !isJoined) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Space is the PTT key; ignore if typing in an input/textarea/contentEditable
      if (e.code !== 'Space') return;
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      e.preventDefault();

      if (!pttActiveRef.current && !isMuted) {
        pttActiveRef.current = true;
        setIsPTTActive(true);
        audioProcessor.startCapture();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      e.preventDefault();

      if (pttActiveRef.current) {
        pttActiveRef.current = false;
        setIsPTTActive(false);
        audioProcessor.stopCapture();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      // Ensure capture is stopped when unmounting or switching modes
      if (pttActiveRef.current) {
        pttActiveRef.current = false;
        audioProcessor.stopCapture();
      }
    };
  }, [inputMode, isJoined, isMuted]);

  /**
   * Handle PTT/VA input mode change.
   * Updated 2026-02-23: Switching from VA to PTT stops continuous capture,
   * switching from PTT to VA restarts capture (if not muted).
   * Persisted to localStorage.
   */
  const handleInputModeChange = useCallback((mode: VoiceInputMode) => {
    const prevMode = inputMode;
    setInputMode(mode);
    saveVoiceSetting(VOICE_SETTINGS_KEYS.INPUT_MODE, mode);
    onInputModeChange?.(mode);

    if (isJoined && !isMuted) {
      if (mode === 'push_to_talk' && prevMode === 'voice_activation') {
        // Switching TO PTT: stop continuous capture; user must hold key
        audioProcessor.stopCapture();
        setIsPTTActive(false);
        pttActiveRef.current = false;
      } else if (mode === 'voice_activation' && prevMode === 'push_to_talk') {
        // Switching TO VA: start continuous capture
        audioProcessor.startCapture();
      }
    }
  }, [inputMode, isJoined, isMuted, onInputModeChange]);

  /**
   * Update connection state and notify parent
   */
  const updateConnectionState = useCallback((state: VoiceConnectionState) => {
    setConnectionState(state);
    onConnectionStateChange?.(state);
  }, [onConnectionStateChange]);

  /**
   * Handle WebSocket events for voice state updates
   * Includes toast notifications for mode changes per Agent_A_Voice_Bridge.md
   */
  useEffect(() => {
    // Subscribe to voice mode change events from server
    // Event name: voice_mode_changed (per contract)
    const handleModeChanged = (data: { 
      session_id: string; 
      mode: VoiceMode; 
      previous_mode?: VoiceMode;
      changed_by?: string;
    }) => {
      // Only process events for our session
      if (data.session_id !== sessionId) return;

      const previousMode = voiceMode;
      setVoiceMode(data.mode);
      onModeChange?.(data.mode);

      // Show toast notification for mode change (per plan Phase 5)
      // Only show if the mode actually changed and it wasn't us who changed it
      if (previousMode !== data.mode) {
        const modeConfig = VOICE_MODES[data.mode];
        if (modeConfig) {
          voiceToastService.showModeChanged(modeConfig.label);
        }
      }
    };

    // Subscribe to connection events
    const handleConnectionReady = () => {
      updateConnectionState(VOICE_CONNECTION_STATE.CONNECTED);
    };

    const handleConnectionClosed = () => {
      updateConnectionState(VOICE_CONNECTION_STATE.DISCONNECTED);
      setIsJoined(false);
    };

    const handleConnectionError = () => {
      updateConnectionState(VOICE_CONNECTION_STATE.ERROR);
      setError(voiceControls.error.connection);
    };

    // Subscribe with correct event names per contract
    webSocketService.subscribe('voice_mode_changed', handleModeChanged);
    webSocketService.subscribe('voice_mode_change', handleModeChanged); // Legacy support
    webSocketService.subscribe('connection_ready', handleConnectionReady);
    webSocketService.subscribe('connection_closed', handleConnectionClosed);
    webSocketService.subscribe('connection_error', handleConnectionError);

    return () => {
      webSocketService.unsubscribe('voice_mode_changed', handleModeChanged);
      webSocketService.unsubscribe('voice_mode_change', handleModeChanged);
      webSocketService.unsubscribe('connection_ready', handleConnectionReady);
      webSocketService.unsubscribe('connection_closed', handleConnectionClosed);
      webSocketService.unsubscribe('connection_error', handleConnectionError);
    };
  }, [sessionId, voiceMode, updateConnectionState, onModeChange]);

  /**
   * Join voice session
   */
  const handleJoin = async () => {
    if (isJoined || isJoining) return;

    setIsJoining(true);
    setError(null);
    updateConnectionState(VOICE_CONNECTION_STATE.CONNECTING);

    try {
      // Initialize audio processor
      audioProcessor.setSessionInfo(sessionId, participantId);
      const micInitialized = await audioProcessor.initializeMicrophone();
      if (!micInitialized) {
        throw new Error(voiceControls.error.micInit);
      }

      // Initialize playback service
      const playbackInitialized = await voicePlaybackService.initialize();
      if (!playbackInitialized) {
        throw new Error(voiceControls.error.playbackInit);
      }

      // Set buffer preset and apply persisted volume
      voicePlaybackService.setBufferPreset(bufferPreset as BufferPreset);
      voicePlaybackService.setVolume(volumeRef.current);

      // The campaign transport is currently snapshot-only, so a rejected voice
      // command cannot be treated as a joined session or permission to capture.
      const joinSent = webSocketService.send('voice.join', {
        sessionId,
        participantId,
        audioConfig: {
          sampleRate: VOICE_BRIDGE_CONFIG.SAMPLE_RATE,
          channels: VOICE_BRIDGE_CONFIG.CHANNELS,
          bitDepth: VOICE_BRIDGE_CONFIG.BIT_DEPTH
        }
      });
      if (!joinSent) {
        // Release resources initialized above. Keep cleanup best-effort so one
        // disposal failure cannot hide the unsent join or produce joined state.
        try { audioProcessor.dispose(); } catch { /* preserve the join failure */ }
        try { voicePlaybackService.dispose(); } catch { /* still report unsent join */ }
        throw new Error(voiceControls.error.connection);
      }

      // Updated 2026-02-23: Only auto-start capture in voice_activation mode.
      // In push_to_talk mode, capture starts when the PTT key is held (Section 5.5).
      if (!isMuted && inputMode === 'voice_activation') {
        const captureStarted = await audioProcessor.startCapture();
        if (!captureStarted) {
          throw new Error(voiceControls.error.captureStart);
        }
      }

      setIsJoined(true);
      updateConnectionState(VOICE_CONNECTION_STATE.CONNECTED);

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : voiceControls.error.joinFailed;
      setError(errorMsg);
      updateConnectionState(VOICE_CONNECTION_STATE.ERROR);
    } finally {
      setIsJoining(false);
    }
  };

  /**
   * Leave voice session
   */
  const handleLeave = () => {
    if (!isJoined) return;

    // Stop capture
    audioProcessor.stopCapture();

    // Stop playback
    voicePlaybackService.stopPlayback();

    // Send leave message to backend
    webSocketService.send('voice.leave', {
      sessionId,
      participantId
    });

    setIsJoined(false);
    updateConnectionState(VOICE_CONNECTION_STATE.DISCONNECTED);
  };

  /**
   * Toggle mute state.
   * Muting stops sending audio via audioProcessor.stopCapture().
   * State is persisted to localStorage for cross-session recall.
   */
  const handleMuteToggle = () => {
    const newMuteState = !isMuted;
    setIsMuted(newMuteState);
    saveVoiceSetting(VOICE_SETTINGS_KEYS.MUTE, newMuteState);

    if (newMuteState) {
      audioProcessor.stopCapture();
    } else if (isJoined) {
      audioProcessor.startCapture();
    }

    onMuteChange?.(newMuteState);
  };

  /**
   * Handle volume slider change.
   * Wires directly to voicePlaybackService output gain node.
   * Persisted to localStorage via the volume useEffect.
   */
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(Math.max(0, Math.min(1, newVolume)));
  };

  /**
   * Change voice mode
   */
  const handleModeChange = (mode: VoiceMode) => {
    setVoiceMode(mode);

    // Send mode change to backend
    webSocketService.send('voice.mode_change', {
      mode,
      sessionId,
      participantId
    });

    onModeChange?.(mode);
  };

  /**
   * Change buffer preset
   */
  const handleBufferPresetChange = (preset: JitterBufferPresetId) => {
    setBufferPreset(preset);
    voicePlaybackService.setBufferPreset(preset as BufferPreset);

    // Signal preference to backend
    const message = BUFFER_PROFILE_MESSAGE.create(preset);
    webSocketService.send(message.type, message.data);
  };

  /**
   * Get connection status display
   */
  const getConnectionStatus = (): { text: string; color: string } => {
    switch (connectionState) {
      case VOICE_CONNECTION_STATE.CONNECTED:
        return { text: voiceControls.status.connected, color: '#22c55e' };
      case VOICE_CONNECTION_STATE.CONNECTING:
        return { text: voiceControls.status.connecting, color: '#eab308' };
      case VOICE_CONNECTION_STATE.RECONNECTING:
        return { text: voiceControls.status.reconnecting, color: '#f97316' };
      case VOICE_CONNECTION_STATE.ERROR:
        return { text: voiceControls.status.error, color: '#ef4444' };
      default:
        return { text: voiceControls.status.disconnected, color: '#6b7280' };
    }
  };

  const status = getConnectionStatus();
  const bufferPresets = getAllJitterBufferPresets();

  return (
    <div className={`voice-controls ${className}`} style={styles.container} role="region" aria-label={voiceControls.region.ariaLabel}>
      {/* Connection Status */}
      <div style={styles.statusBar} role="status" aria-live="polite" aria-atomic="true">
        <span style={{ ...styles.statusDot, backgroundColor: status.color }} aria-hidden="true" />
        <span style={compact ? styles.srOnly : styles.statusText}>{status.text}</span>
      </div>

      {/* Error Display */}
      {error && (
        <div style={styles.error} role="alert" aria-live="polite" id="voice-error-message">
          ❌ {error}
          <button 
            onClick={() => setError(null)} 
            style={styles.dismissError}
            aria-label={voiceControls.error.ariaLabel}
            aria-describedby="voice-error-message"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Controls */}
      <div style={styles.controlsRow} role="group" aria-label={voiceControls.controlsGroup.ariaLabel}>
        {/* Join/Leave Button */}
        {!isJoined ? (
          <button
            onClick={handleJoin}
            disabled={isJoining}
            style={{
              ...styles.button,
              ...styles.joinButton,
              opacity: isJoining ? 0.7 : 1
            }}
            aria-label={isJoining ? voiceControls.join.ariaLabelJoining : voiceControls.join.ariaLabel}
            title={voiceControls.join.title}
          >
            {isJoining ? '⏳' : '📞'} {!compact && (isJoining ? voiceControls.join.joining : voiceControls.join.label)}
          </button>
        ) : (
          <button
            onClick={handleLeave}
            style={{ ...styles.button, ...styles.leaveButton }}
            aria-label={voiceControls.leave.ariaLabel}
            title={voiceControls.leave.title}
          >
            📵 {!compact && voiceControls.leave.label}
          </button>
        )}

        {/* Mute Button */}
        {isJoined && (
          <button
            onClick={handleMuteToggle}
            style={{
              ...styles.button,
              ...(isMuted ? styles.mutedButton : styles.unmutedButton)
            }}
            aria-label={isMuted ? voiceControls.mute.ariaLabelUnmute : voiceControls.mute.ariaLabelMute}
            aria-pressed={isMuted}
            title={isMuted ? voiceControls.mute.titleUnmute : voiceControls.mute.titleMute}
          >
            {isMuted ? '🔇' : '🎤'} {!compact && (isMuted ? voiceControls.mute.unmute : voiceControls.mute.label)}
          </button>
        )}
      </div>

      {/* Mode Selector */}
      {isJoined && (
        <div style={styles.modeSelector} role="radiogroup" aria-label={voiceControls.mode.ariaLabelGroup}>
          {!compact && <label style={styles.label} htmlFor="voice-mode-buttons">{voiceControls.mode.label}</label>}
          <div style={styles.modeButtons} id="voice-mode-buttons">
            {Object.values(VOICE_MODES).map((mode) => (
              <button
                key={mode.id}
                onClick={() => handleModeChange(mode.id)}
                style={{
                  ...styles.modeButton,
                  ...(voiceMode === mode.id ? styles.modeButtonActive : {})
                }}
                role="radio"
                aria-checked={voiceMode === mode.id}
                aria-label={`${mode.label}: ${mode.description}`}
                title={mode.description}
              >
                {mode.icon} {!compact && mode.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* PTT/VA Input Mode Toggle (Section 5.5) */}
      {/* Updated 2026-02-23: Push-to-Talk vs Voice Activation selector */}
      {isJoined && (
        <div style={styles.inputModeSelector} role="radiogroup" aria-label={voiceControls.inputMode.ariaLabelGroup}>
          {!compact && <label style={styles.label}>{voiceControls.inputMode.label}</label>}
          <div style={styles.modeButtons}>
            <button
              onClick={() => handleInputModeChange('voice_activation')}
              style={{
                ...styles.modeButton,
                ...(inputMode === 'voice_activation' ? styles.modeButtonActive : {})
              }}
              role="radio"
              aria-checked={inputMode === 'voice_activation'}
              aria-label={voiceControls.inputMode.voiceActivation.ariaLabel}
              title={voiceControls.inputMode.voiceActivation.description}
            >
              {compact ? voiceControls.inputMode.voiceActivation.shortLabel : voiceControls.inputMode.voiceActivation.label}
            </button>
            <button
              onClick={() => handleInputModeChange('push_to_talk')}
              style={{
                ...styles.modeButton,
                ...(inputMode === 'push_to_talk' ? styles.modeButtonActive : {}),
                ...(isPTTActive ? styles.pttActiveButton : {})
              }}
              role="radio"
              aria-checked={inputMode === 'push_to_talk'}
              aria-label={voiceControls.inputMode.ptt.ariaLabel}
              title={voiceControls.inputMode.ptt.description}
            >
              {compact ? voiceControls.inputMode.ptt.shortLabel : voiceControls.inputMode.ptt.label}
            </button>
          </div>
          {/* Contextual hint for the active input mode */}
          {!compact && (
            <div style={styles.inputModeHint} aria-live="polite">
              {inputMode === 'push_to_talk'
                ? (isPTTActive ? '🔴 Transmitting...' : voiceControls.inputMode.ptt.activeHint)
                : voiceControls.inputMode.voiceActivation.activeHint}
            </div>
          )}
        </div>
      )}

      {/* Volume Slider */}
      {isJoined && showVolumeSlider && !compact && (
        <div style={styles.volumeSlider}>
          <label style={styles.label} htmlFor="voice-volume-slider">
            {/* Added 2026-02-23: replaced hardcoded "Volume:" with voiceControls constant */}
            {volume === 0 ? '🔇' : volume < 0.5 ? '🔈' : '🔊'} {voiceControls.volume.label} {Math.round(volume * 100)}%
          </label>
          <input
            id="voice-volume-slider"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            style={styles.slider}
            aria-label={formatMessage(voiceControls.volume.ariaLabel, { percent: Math.round(volume * 100) })}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(volume * 100)}
            aria-valuetext={formatMessage(voiceControls.volume.valueText, { percent: Math.round(volume * 100) })}
          />
        </div>
      )}

      {/* Buffer Preset Selector (optional) */}
      {/* TODO: [4.1.2 Help Text] Add inline help tooltip explaining what buffer presets do
          (e.g. "Responsive = lower latency but may stutter; Smooth = more stable but higher delay").
          Could reuse the tooltip pattern from VoiceBridgeInfo. */}
      {isJoined && showBufferPreset && (
        <div style={styles.bufferSelector}>
          {!compact && <label style={styles.label} htmlFor="buffer-preset-select">{voiceControls.buffer.label}</label>}
          <select
            id="buffer-preset-select"
            value={bufferPreset}
            onChange={(e) => handleBufferPresetChange(e.target.value as JitterBufferPresetId)}
            style={styles.select}
            aria-label={voiceControls.buffer.ariaLabel}
            title={voiceControls.buffer.title}
          >
            {bufferPresets.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.label} ({preset.bufferMs}ms)
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

/**
 * Component styles
 * Using inline styles for encapsulation; can be moved to CSS module
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '12px',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    fontFamily: '"Cinzel", serif'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    animation: 'pulse 2s ease-in-out infinite'
  },
  statusText: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderRadius: '8px',
    color: '#fca5a5',
    fontSize: '13px'
  },
  dismissError: {
    background: 'none',
    border: 'none',
    color: '#fca5a5',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '0 4px'
  },
  controlsRow: {
    display: 'flex',
    gap: '8px'
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    fontFamily: '"Cinzel", serif'
  },
  joinButton: {
    backgroundColor: '#22c55e',
    color: '#fff'
  },
  leaveButton: {
    backgroundColor: '#ef4444',
    color: '#fff'
  },
  mutedButton: {
    backgroundColor: '#6b7280',
    color: '#fff'
  },
  unmutedButton: {
    backgroundColor: '#3b82f6',
    color: '#fff'
  },
  modeSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  modeButtons: {
    display: 'flex',
    gap: '4px'
  },
  modeButton: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.8)',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s ease',
    fontFamily: '"Cinzel", serif'
  },
  modeButtonActive: {
    backgroundColor: 'rgba(139, 92, 246, 0.3)',
    borderColor: '#8b5cf6',
    color: '#fff'
  },
  volumeSlider: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  slider: {
    width: '100%',
    height: '4px',
    appearance: 'auto' as any,
    cursor: 'pointer',
    accentColor: '#8b5cf6'
  },
  bufferSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  select: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    color: '#fff',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: '"Cinzel", serif'
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0
  },
  // Updated 2026-02-23: PTT/VA toggle styles (Section 5.5)
  inputModeSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  inputModeHint: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)',
    fontStyle: 'italic'
  },
  pttActiveButton: {
    backgroundColor: 'rgba(239, 68, 68, 0.4)',
    borderColor: '#ef4444',
    color: '#fff',
    boxShadow: '0 0 8px rgba(239, 68, 68, 0.4)'
  }
};

export default VoiceControls;
